import API from '@/api';
import get from 'lodash/get';
import { mapGetters } from 'vuex';

export default {
  data() {
    return {
      signatureStatuses: [
        {
          value: 'sending',
          text: 'Sending',
          icon: 'email',
          class: 'grey--text',
        },
        {
          value: 'sent',
          text: 'Sent',
          icon: 'email',
          class: 'primary--text',
        },
        {
          value: 'signed',
          text: 'Signed',
          icon: 'check_circle',
          class: 'success--text',
        },
        {
          value: 'completed',
          text: 'Completed',
          icon: 'done_all',
          class: 'success--text',
        },
        {
          value: 'error',
          text: 'Error',
          icon: 'cancel',
          class: 'error--text',
        },
        {
          value: 'rejected',
          text: 'Rejected',
          icon: 'cancel',
          class: 'error--text',
        },
        {
          value: 'processing',
          text: 'Processing',
          icon: 'circle',
          class: 'grey--text',
        },
        {
          value: 'action_required',
          text: 'Action required',
          icon: 'warning',
          class: 'warning--text',
        },
      ],
    };
  },

  computed: {
    ...mapGetters({
      currentUser: 'user',
    }),
  },

  methods: {
    getSignatureStatus(signature) {
      return this.signatureStatuses.find(s => s.value == signature.status);
    },

    async resend(item) {
      try {
        const subject = item.subject
          ? item.subject
          : 'Please sign ' + this.getName(item);

        const message = item.message
          ? item.message
          : `Hi {client_preferred_name},\nPlease click the button below to review the document and sign it. Thank you very much.\n\nBest regards,\n${this.currentUser.full_name}`;

        await API.signatureRequest.sendSignatureRequest({
          companyId: item.company_id,
          id: item.id,
          signatures: item.signatures,
          pages: item.pages,
          subject: subject,
          message: message,
          includeWatermark: item.includeWatermark,
        });

        this.$store.dispatch('application/setSuccessNotification', {
          message: `You have successfully sent signature requests!`,
        });
      } catch (e) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: `There was a problem sending signature request.`,
          errorToSend: e,
          userEmail: this.currentUser.email,
        });
      }
    },

    async remove(item) {
      await API.signatureRequest.deleteSignatureRequest({
        id: item.id,
        companyId: item.company_id,
      });

      await this.refreshTable();
    },

    async markAsCompleted(item) {
      await API.signatureRequest.updateSignatureRequest({
        id: item.id,
        companyId: item.company_id,
        status: 'completed',
      });

      await this.refreshTable();
    },

    getName(item) {
      if (item.form_name) {
        return item.form_name;
      } else {
        return this.getDocumentName(item.source);
      }
    },

    async downloadDocument(item) {
      try {
        const source = item.signed_document
          ? item.signed_document
          : item.source;

        const response1 = await API.integration.downloadDocument({
          companyId: item.company_id,
          link: source,
        });

        const name = this.getDocumentName(source);

        this.downloadDocumentByLink(response1.data.document.data.link, name);
      } catch (e) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was a problem downloading the document.',
        });
      }
    },

    async downloadSignedPagesOnly(item) {
      try {
        const source = item.signed_pages_only;

        const response1 = await API.integration.downloadDocument({
          companyId: item.company_id,
          link: source,
        });

        const name = this.getDocumentName(source);

        this.downloadDocumentByLink(response1.data.document.data.link, name);
      } catch (e) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was a problem downloading the document.',
        });
      }
    },

    getDocumentName(document) {
      return document.split('/')[document.split('/').length - 1];
    },

    async downloadDocumentByLink(url, name) {
      const response = await this.$http.get(url, {
        responseType: 'arraybuffer',
      });
      const contentType = get(response.headers, 'content-type');
      const blob = new Blob([response.data], { type: contentType });
      const objectUrl = window.URL.createObjectURL(blob);

      // Trigger download through a temporary link so it works inside iframes.
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setTimeout(() => window.URL.revokeObjectURL(objectUrl), 1000);
    },

    downloadInIframe(blob, name) {
      const blobUrl = window.URL.createObjectURL(blob);

      try {
        // Method 1: Try window.open() - works in most iframe contexts
        const downloadWindow = window.open(blobUrl, '_blank');
        if (downloadWindow) {
          // Set a timeout to clean up the blob URL after the download likely completed
          setTimeout(() => {
            window.URL.revokeObjectURL(blobUrl);
            downloadWindow.close();
          }, 1000);
          return;
        }
      } catch (e) {
        console.log('window.open method failed:', e);
      }

      try {
        // Method 2: Try creating and clicking a link with target="_blank"
        let link = document.createElement('a');
        link.href = blobUrl;
        link.download = name;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';

        // Add to DOM temporarily to ensure click works
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Clean up blob URL after a delay
        setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
        return;
      } catch (e) {
        console.log('Enhanced link click method failed:', e);
      }

      // Method 3: Fallback - notify user with direct link
      this.showDownloadFallback(blobUrl, name);
    },

    showDownloadFallback(blobUrl, name) {
      // Create a temporary notification/dialog for manual download
      const message = `Please right-click this link and select "Save link as..." to download ${name}`;

      // Try to use the app's notification system if available
      if (this.$store && this.$store.dispatch) {
        this.$store.dispatch('application/setSuccessNotification', {
          message: message,
          action: {
            text: 'Download',
            handler: () => {
              const link = document.createElement('a');
              link.href = blobUrl;
              link.download = name;
              link.target = '_blank';
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
            },
          },
        });
      } else {
        // Fallback: show browser alert with instructions
        alert(
          `Download blocked by browser security. Please contact support or try downloading in table view.`,
        );
        window.URL.revokeObjectURL(blobUrl);
      }
    },
  },
};
